import { CollectionSVG } from "@newsela/angelou";

import { useNavigationBarV2Context } from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.provider";
import {
  EXPLORE_CONTENT_ITEMS,
  MY_CONTENT_ITEMS,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";
import { PopoutMenuSection } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutMenuSection";
import { PopoutSectionItem } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutSectionItem";
import {
  PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
} from "@/components/ContentPromotions/constants";
import useHasFeature from "@/features/useHasFeature";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { HomeMenuSubItemPopout } from "@/base/NewselaGlobalLayoutV2/NavigationBar/HomeMenu/HomeMenuSubItemPopout";

const HomeMenuContentLibraryPopout = ({
  onClickBack,
}: IHomeContentLibraryPopout) => {
  const { data: user } = useUser();
  const { isStudent, isLiteUser } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });
  const {
    getters: { customCollections, personalizedCollections },
  } = useNavigationBarV2Context();

  const [hasUserFeatureToAccessPromotedAtYourDistrictPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  );
  const [hasUserFeatureToAccessPromotedAtYourSchoolPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
  );

  return (
    <HomeMenuSubItemPopout
      dataTestId="home-content-library-popout"
      onClickBack={onClickBack}
    >
      <PopoutMenuSection title="My content" width="15.625rem">
        {MY_CONTENT_ITEMS.filter(
          (item) =>
            !(
              item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district &&
              !hasUserFeatureToAccessPromotedAtYourDistrictPage
            ),
        )
          .filter(
            (item) =>
              !(
                item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school &&
                !hasUserFeatureToAccessPromotedAtYourSchoolPage
              ),
          )
          .filter((item) => !isStudent || item.showForStudents)
          .map(
            ({ title, href, SvgComponent, dataQASelector, badge }, index) => (
              <PopoutSectionItem
                key={index}
                href={href}
                title={title}
                badge={badge}
                SvgComponent={SvgComponent}
                dataQASelector={dataQASelector}
              />
            ),
          )}
        {personalizedCollections.map(({ title, href }) => (
          <PopoutSectionItem
            key={title}
            href={href}
            title={title}
            SvgComponent={CollectionSVG}
            dataQASelector={
              title ? `global-nav-${title.toLowerCase().replace(" ", "-")}` : ""
            }
          />
        ))}
        {customCollections.map(({ title, href }) => (
          <PopoutSectionItem
            key={title}
            href={href}
            title={title}
            SvgComponent={CollectionSVG}
          />
        ))}
      </PopoutMenuSection>

      <PopoutMenuSection title="Explore content" width="15.625rem">
        {EXPLORE_CONTENT_ITEMS.filter(
          (item) =>
            (!isStudent || item.showForStudents) &&
            (!isLiteUser || item.showForLite),
        ).map(
          (
            { title, href, SvgComponent, openInNewTab, badge, dataQASelector },
            index,
          ) => (
            <PopoutSectionItem
              key={index}
              href={href}
              title={title}
              SvgComponent={SvgComponent}
              dataQASelector={dataQASelector}
              openInNewTab={openInNewTab}
              badge={badge}
            />
          ),
        )}
      </PopoutMenuSection>
    </HomeMenuSubItemPopout>
  );
};

interface IHomeContentLibraryPopout {
  onClickBack: () => void;
}

HomeMenuContentLibraryPopout.displayName = "HomeContentLibraryPopout";
export { HomeMenuContentLibraryPopout };
